/**
 * Utility helpers for the Gestionale application
 */

// Currency formatting
export const currency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(value || 0);
};

// Date formatting — Italian DD/MM/YYYY (zero-padded). Explicit 2-digit
// options guarantee the same output across browsers/ICU versions; without
// them some engines render "17/5/2026" instead of "17/05/2026".
export const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// DateTime formatting
export const formatDateTime = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleString("it-IT");
};

/**
 * Replace any ISO date token (YYYY-MM-DD) found inside a free-text
 * string with its Italian localized equivalent (DD/MM/YYYY).
 *
 * Used as a defensive display-time fallback when a backend message
 * (e.g. a notification `content` body) embeds raw ISO dates inside an
 * otherwise human-readable string. Backend stays the single source of
 * truth for storage (ISO), the UI is just rendering it correctly.
 *
 * Safe to call on any value — strings without an ISO token pass through
 * unchanged; non-strings (null, undefined, numbers) are returned as-is.
 */
export const localizeIsoDatesInText = (text) => {
  if (typeof text !== "string" || !text) return text;
  return text.replace(
    /\b(\d{4})-(\d{2})-(\d{2})\b/g,
    (_match, y, m, d) => `${d}/${m}/${y}`
  );
};

/**
 * Compose the office-spec invoice PDF filename.
 *
 *   "Fattura <invoiceNumber> del <DDMMYYYY> <clientName>.pdf"
 *
 * Mirrors the backend helper `build_invoice_pdf_filename` in
 * /app/backend/server.py so the file shown in the browser, the
 * "Save as..." dialog, the download endpoint and the preview modal
 * are byte-identical.
 *
 * Sanitises Windows-forbidden chars (\\ / : * ? " < > |) and collapses
 * runs of whitespace.
 */
export const buildInvoicePdfFilename = ({ invoiceNumber, invoiceDate, clientName } = {}) => {
  const forbidden = /[\\/:*?"<>|\r\n\t]/g;
  const collapse = /\s+/g;
  const safe = (s) => (s == null ? "" : String(s).replace(forbidden, " ").replace(collapse, " ").trim());

  const inv = safe(invoiceNumber) || "Senza-Numero";
  let dt = "";
  if (invoiceDate) {
    const raw = String(invoiceDate).trim();
    let d, m, y;
    if (raw.includes("T")) {
      [y, m, d] = raw.split("T")[0].split("-");
    } else if (raw.includes("-") && raw.length >= 10) {
      [y, m, d] = raw.slice(0, 10).split("-");
    } else if (raw.includes("/") && raw.length >= 10) {
      [d, m, y] = raw.slice(0, 10).split("/");
    }
    if (d && m && y) {
      dt = `${String(parseInt(d, 10)).padStart(2, "0")}${String(parseInt(m, 10)).padStart(2, "0")}${String(parseInt(y, 10)).padStart(4, "0")}`;
    } else {
      dt = safe(raw);
    }
  }
  const name = safe(clientName) || "Cliente";
  let base = dt ? `Fattura ${inv} del ${dt} ${name}` : `Fattura ${inv} ${name}`;
  base = base.replace(collapse, " ").trim();
  return `${base}.pdf`;
};

/**
 * Format phone number for display
 * Formats Italian phone numbers with proper spacing
 * Examples:
 *   3331234567 → 333 123 4567
 *   +393331234567 → +39 333 123 4567
 *   0612345678 → 06 1234 5678
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, "");
  
  // Handle international prefix
  let prefix = "";
  if (cleaned.startsWith("+39")) {
    prefix = "+39 ";
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith("0039")) {
    prefix = "+39 ";
    cleaned = cleaned.substring(4);
  }
  
  // Format based on length and type
  if (cleaned.startsWith("0")) {
    // Landline (starts with 0)
    if (cleaned.length === 10) {
      // Format: 06 1234 5678
      return prefix + cleaned.substring(0, 2) + " " + cleaned.substring(2, 6) + " " + cleaned.substring(6);
    } else if (cleaned.length === 11) {
      // Format: 081 123 4567
      return prefix + cleaned.substring(0, 3) + " " + cleaned.substring(3, 6) + " " + cleaned.substring(6);
    }
  } else if (cleaned.startsWith("3")) {
    // Mobile (starts with 3)
    if (cleaned.length === 10) {
      // Format: 333 123 4567
      return prefix + cleaned.substring(0, 3) + " " + cleaned.substring(3, 6) + " " + cleaned.substring(6);
    }
  }
  
  // Default: group in 3-3-4 or return as-is with spaces every 3-4 digits
  if (cleaned.length >= 9) {
    return prefix + cleaned.substring(0, 3) + " " + cleaned.substring(3, 6) + " " + cleaned.substring(6);
  }
  
  return prefix + cleaned;
};

/**
 * Format phone number on input (for controlled inputs)
 * Adds spacing as user types
 */
export const formatPhoneOnInput = (value) => {
  if (!value) return "";
  
  // Remove all non-digit characters except +
  let cleaned = value.replace(/[^\d+]/g, "");
  
  // Handle + prefix
  let prefix = "";
  if (cleaned.startsWith("+")) {
    prefix = "+";
    cleaned = cleaned.substring(1);
  }
  
  // Add spacing progressively
  let formatted = prefix;
  if (cleaned.length > 0) {
    if (prefix === "+") {
      // International: +39 333 123 4567
      if (cleaned.length <= 2) {
        formatted += cleaned;
      } else if (cleaned.length <= 5) {
        formatted += cleaned.substring(0, 2) + " " + cleaned.substring(2);
      } else if (cleaned.length <= 8) {
        formatted += cleaned.substring(0, 2) + " " + cleaned.substring(2, 5) + " " + cleaned.substring(5);
      } else {
        formatted += cleaned.substring(0, 2) + " " + cleaned.substring(2, 5) + " " + cleaned.substring(5, 8) + " " + cleaned.substring(8, 12);
      }
    } else {
      // National: 333 123 4567
      if (cleaned.length <= 3) {
        formatted += cleaned;
      } else if (cleaned.length <= 6) {
        formatted += cleaned.substring(0, 3) + " " + cleaned.substring(3);
      } else {
        formatted += cleaned.substring(0, 3) + " " + cleaned.substring(3, 6) + " " + cleaned.substring(6, 10);
      }
    }
  }
  
  return formatted;
};

/**
 * Capitalize first letter of a string
 * Used for auto-capitalization in text inputs
 */
export const capitalizeFirst = (value) => {
  if (!value || typeof value !== "string") return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Handle input change with auto-capitalization
 * For use in onChange handlers
 */
export const handleCapitalizedInput = (value, setter) => {
  setter(capitalizeFirst(value));
};

/**
 * Format Codice Fiscale on input
 * - Converts to uppercase
 * - Removes spaces and non-alphanumeric characters
 * - Max 16 characters
 */
export const formatCodiceFiscaleOnInput = (value) => {
  if (!value) return "";
  // Remove non-alphanumeric, convert to uppercase, limit to 16 chars
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 16);
};

/**
 * Validate Codice Fiscale format
 * Returns { valid: boolean, error: string | null }
 * 
 * Italian CF structure:
 * - 6 letters (surname + name)
 * - 2 digits (year)
 * - 1 letter (month)
 * - 2 digits (day + gender)
 * - 4 chars (municipality code)
 * - 1 letter (checksum)
 *
 * OMOCODIA: when two people would otherwise share the same CF the
 * Agenzia delle Entrate disambiguates by replacing one or more of the
 * numeric digits in positions 7-8, 10-11, 13-15 with a letter via the
 * fixed mapping L→0 M→1 N→2 P→3 Q→4 R→5 S→6 T→7 U→8 V→9. The checksum
 * is then computed on the DECODED (all-numeric) form. We therefore:
 *   1. accept letters in those positions at the regex level;
 *   2. decode them back to digits BEFORE running the checksum;
 *   3. expose the decoded CF in the response so the caller can compare
 *      against a known birth date / municipality without surprises.
 */
const OMOCODIA_LETTER_TO_DIGIT = {
  L: "0", M: "1", N: "2", P: "3", Q: "4",
  R: "5", S: "6", T: "7", U: "8", V: "9",
};

// Positions that may contain omocodia substitutions (0-based).
const OMOCODIA_POSITIONS = [6, 7, 9, 10, 12, 13, 14];

function decodeOmocodia(cleaned) {
  const chars = cleaned.split("");
  for (const idx of OMOCODIA_POSITIONS) {
    const ch = chars[idx];
    if (ch in OMOCODIA_LETTER_TO_DIGIT) {
      chars[idx] = OMOCODIA_LETTER_TO_DIGIT[ch];
    }
  }
  return chars.join("");
}

export const validateCodiceFiscale = (cf) => {
  if (!cf) return { valid: true, error: null }; // Optional field
  
  const cleaned = cf.toUpperCase().replace(/[^A-Z0-9]/g, "");
  
  if (cleaned.length !== 16) {
    return { 
      valid: false, 
      error: `Lunghezza non valida (${cleaned.length}/16 caratteri)` 
    };
  }
  
  // Structure: 6 letters, then 7 alphanumerics in the omocodia-eligible
  // slots (positions 7-8, 10-11, 13-15), with fixed letters at month
  // (pos 9) and municipality prefix (pos 12), and a checksum letter
  // at pos 16. The omocodia-eligible slots accept either digits or one
  // of the {L,M,N,P,Q,R,S,T,U,V} substitution letters.
  const omoChar = "[0-9LMNPQRSTUV]";
  const cfRegex = new RegExp(
    "^[A-Z]{6}" +
    omoChar + "{2}" +   // year digits (pos 7-8)
    "[A-Z]" +           // month letter (pos 9)
    omoChar + "{2}" +   // day digits (pos 10-11)
    "[A-Z]" +           // municipality prefix (pos 12)
    omoChar + "{3}" +   // municipality digits (pos 13-15)
    "[A-Z]$"            // checksum letter (pos 16)
  );
  if (!cfRegex.test(cleaned)) {
    return { 
      valid: false, 
      error: "Formato non valido" 
    };
  }

  // Decode any omocodia substitutions before computing the checksum.
  const decoded = decodeOmocodia(cleaned);
  
  // Optional: Checksum validation
  const checksumChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const oddMap = {
    "0": 1, "1": 0, "2": 5, "3": 7, "4": 9, "5": 13, "6": 15, "7": 17, "8": 19, "9": 21,
    "A": 1, "B": 0, "C": 5, "D": 7, "E": 9, "F": 13, "G": 15, "H": 17, "I": 19, "J": 21,
    "K": 2, "L": 4, "M": 18, "N": 20, "O": 11, "P": 3, "Q": 6, "R": 8, "S": 12, "T": 14,
    "U": 16, "V": 10, "W": 22, "X": 25, "Y": 24, "Z": 23
  };
  const evenMap = {
    "0": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9,
    "A": 0, "B": 1, "C": 2, "D": 3, "E": 4, "F": 5, "G": 6, "H": 7, "I": 8, "J": 9,
    "K": 10, "L": 11, "M": 12, "N": 13, "O": 14, "P": 15, "Q": 16, "R": 17, "S": 18, "T": 19,
    "U": 20, "V": 21, "W": 22, "X": 23, "Y": 24, "Z": 25
  };
  
  // CRITICAL: checksum is computed on the ORIGINAL (still-omocodified)
  // CF, not on the decoded one. The Agenzia delle Entrate spec defines
  // the algorithm in terms of the printed CF characters, and oddMap
  // already provides identical values for L/M/N/P/Q/R/S/T/U/V vs their
  // 0/1/2/3/4/5/6/7/8/9 counterparts respectively. Using `cleaned` is
  // therefore both correct AND robust against any future change to
  // OMOCODIA_LETTER_TO_DIGIT.
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const char = cleaned[i];
    sum += (i % 2 === 0) ? oddMap[char] : evenMap[char];
  }
  
  const expectedChecksum = checksumChars[sum % 26];
  if (cleaned[15] !== expectedChecksum) {
    return { 
      valid: false, 
      error: "Codice di controllo non valido" 
    };
  }
  
  return { valid: true, error: null, decoded };
};

/**
 * Format Partita IVA on input
 * - Removes non-numeric characters
 * - Max 11 digits
 * - Optional: adds spacing for readability (123 456 78901)
 */
export const formatPartitaIVAOnInput = (value, withSpacing = false) => {
  if (!value) return "";
  
  // Remove non-numeric characters, limit to 11 digits
  const cleaned = value.replace(/\D/g, "").slice(0, 11);
  
  if (!withSpacing) return cleaned;
  
  // Format with spacing: 123 456 78901
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return cleaned.slice(0, 3) + " " + cleaned.slice(3);
  return cleaned.slice(0, 3) + " " + cleaned.slice(3, 6) + " " + cleaned.slice(6);
};

/**
 * Format Partita IVA for display
 * Formats as: 123 456 78901
 */
export const formatPartitaIVA = (piva) => {
  if (!piva) return "";
  const cleaned = piva.replace(/\D/g, "");
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return cleaned.slice(0, 3) + " " + cleaned.slice(3);
  return cleaned.slice(0, 3) + " " + cleaned.slice(3, 6) + " " + cleaned.slice(6);
};

/**
 * Validate Partita IVA format and checksum
 * Returns { valid: boolean, error: string | null }
 * 
 * Italian VAT number validation with Luhn-like checksum
 */
export const validatePartitaIVA = (piva) => {
  if (!piva) return { valid: true, error: null }; // Optional field
  
  const cleaned = piva.replace(/\D/g, "");
  
  if (cleaned.length !== 11) {
    return { 
      valid: false, 
      error: `Lunghezza non valida (${cleaned.length}/11 cifre)` 
    };
  }
  
  // Checksum validation (Italian VAT algorithm)
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let digit = parseInt(cleaned[i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  
  if (sum % 10 !== 0) {
    return { 
      valid: false, 
      error: "Partita IVA non valida (checksum errato)" 
    };
  }
  
  return { valid: true, error: null };
};

// Generate case number (legacy, now handled by backend)
export const generateCaseNumber = (count) => {
  const year = new Date().getFullYear();
  const num = String(count + 1).padStart(4, "0");
  return `${year}-${num}`;
};

// Generate invoice number
export const generateInvoiceNumber = (count) => {
  const year = new Date().getFullYear();
  const num = String(count + 1).padStart(4, "0");
  return `FT-${year}-${num}`;
};

// Add days to a date
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Get days until a date
export const getDaysUntil = (date) => {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffTime = target - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Deadline urgency calculator
export const getDeadlineUrgency = (dueDate, status, deadlineUrgencyConfig) => {
  if (status === "Completata") return { color: "green", label: "Completata" };
  if (!dueDate) return deadlineUrgencyConfig.normal;
  const days = getDaysUntil(dueDate);
  if (days < 0) return deadlineUrgencyConfig.overdue;
  if (days <= 3) return deadlineUrgencyConfig.critical;
  if (days <= 7) return deadlineUrgencyConfig.warning;
  return deadlineUrgencyConfig.normal;
};

// Generate deadlines for a case based on rules
export const generateDeadlinesForCase = (caseItem, deadlineRules) => {
  const rules = deadlineRules[caseItem.practiceArea];
  if (!rules) return [];

  const today = new Date();
  return rules.map((rule) => ({
    caseId: caseItem.id,
    title: rule.name,
    dueDate: addDays(today, rule.days).toISOString().split("T")[0],
    status: "Pending",
    priority: rule.priority,
    notes: `Scadenza automatica per ${caseItem.practiceArea}`,
  }));
};
